/**
 * Shared Utilities
 *
 * Common utility functions used across the monorepo.
 *
 * @module @loader/shared-types/utils
 */

import {
  ALLOWED_FILE_EXTENSIONS,
  ALLOWED_MIME_TYPES,
  type AllowedFileExtension,
  type AllowedMimeType,
} from '../constants/files';

// Qualified field ID utilities (for duplicate table joins)
export * from './qualifiedFieldId';

// Error handling utilities
export * from './error-utils';

// Date conversion utilities
export * from './date-conversion';

// HMAC signing utilities
export * from './hmac';

// NOTE: formatters.ts is NOT re-exported here because it has heavy dependencies (xlsx, fast-xml-parser).
// Import directly from '@loader/shared-types/utils/formatters' if you need export format utilities.

// ============================================================================
// STORAGE & FILE SIZE FORMATTING
// ============================================================================

/**
 * Format storage limit from MB to GB string
 *
 * Used for displaying plan storage limits in billing/subscription UI.
 *
 * @param mb - Storage limit in megabytes
 * @returns Formatted storage string (e.g., "5 GB", "Unlimited")
 *
 * @example
 * ```ts
 * formatStorageLimit(5120) // "5 GB"
 * formatStorageLimit(10240) // "10 GB"
 * formatStorageLimit(null) // "Unlimited"
 * ```
 */
export function formatStorageLimit(mb: number | null): string {
  if (mb === null) return 'Unlimited';
  const gb = mb / 1024;
  return `${gb.toFixed(0)} GB`;
}

/**
 * Format file size from bytes to human-readable string
 *
 * Handles edge cases: undefined, null, NaN, negative values, and very large files.
 *
 * @param bytes - File size in bytes
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted file size string (e.g., "1.50 MB", "500 KB")
 *
 * @example
 * ```ts
 * formatFileSize(1536) // "1.50 KB"
 * formatFileSize(1572864) // "1.50 MB"
 * formatFileSize(1610612736) // "1.50 GB"
 * formatFileSize(1536, 0) // "2 KB"
 * formatFileSize(undefined) // "0 Bytes"
 * formatFileSize(NaN) // "0 Bytes"
 * formatFileSize(0) // "0 Bytes"
 * ```
 */
export function formatFileSize(
  bytes: number | undefined | null,
  decimals: number = 2
): string {
  // Handle invalid values (undefined, null, NaN, negative)
  if (bytes == null || !Number.isFinite(bytes) || bytes < 0) {
    return '0 Bytes';
  }

  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
}

/**
 * Alias for formatFileSize for backwards compatibility
 *
 * @see formatFileSize
 */
export const formatBytes = formatFileSize;

// ============================================================================
// FILE VALIDATION UTILITIES
// ============================================================================

/**
 * Extract file extension from filename (without dot)
 *
 * @param filename - The filename to extract extension from
 * @returns Lowercase file extension without the dot, or empty string if no extension
 *
 * @example
 * ```ts
 * getFileExtension('data.csv') // 'csv'
 * getFileExtension('report.json') // 'json'
 * getFileExtension('file') // ''
 * getFileExtension('archive.tar.gz') // 'gz'
 * ```
 */
export function getFileExtension(filename: string): string {
  const parts = filename.split('.');
  if (parts.length < 2) {
    return '';
  }
  return parts[parts.length - 1].toLowerCase();
}

/**
 * Check if a file extension is allowed for uploads
 *
 * @param extension - The file extension to validate (without dot)
 * @returns True if the extension is in the allowed list
 *
 * @example
 * ```ts
 * isValidFileExtension('csv') // true
 * isValidFileExtension('pdf') // false
 * isValidFileExtension('CSV') // true (case-insensitive)
 * ```
 */
export function isValidFileExtension(extension: string): extension is AllowedFileExtension {
  const normalizedExt = extension.toLowerCase();
  return ALLOWED_FILE_EXTENSIONS.includes(normalizedExt as AllowedFileExtension);
}

/**
 * Check if a MIME type is allowed for uploads
 *
 * @param mimeType - The MIME type to validate (can include charset or other params)
 * @returns True if the MIME type is in the allowed list
 *
 * @example
 * ```ts
 * isValidMimeType('text/csv') // true
 * isValidMimeType('text/csv; charset=utf-8') // true
 * isValidMimeType('application/pdf') // false
 * ```
 */
export function isValidMimeType(mimeType: string): mimeType is AllowedMimeType {
  // Extract the base MIME type (remove charset and other params)
  const baseMimeType = mimeType.split(';')[0].trim().toLowerCase();
  return ALLOWED_MIME_TYPES.includes(baseMimeType as AllowedMimeType);
}

// ============================================================================
// SQL IDENTIFIER UTILITIES
// ============================================================================

/**
 * Escape double quotes in a SQL identifier by doubling them
 *
 * This is the SQL standard way to include a literal double quote in an identifier.
 * Use this when you need to escape but will wrap in quotes separately.
 *
 * @param name - The identifier name to escape
 * @returns The escaped identifier (without surrounding quotes)
 *
 * @example
 * ```ts
 * escapeDoubleQuotes('user_name') // 'user_name'
 * escapeDoubleQuotes('user"name') // 'user""name'
 * escapeDoubleQuotes('First Name') // 'First Name'
 * ```
 */
export function escapeDoubleQuotes(name: string): string {
  return name.replace(/"/g, '""');
}

/**
 * Quote a SQL identifier with proper escaping
 *
 * Wraps the identifier in double quotes and escapes any embedded double quotes.
 * This is the complete operation for safely using an identifier in SQL.
 *
 * @param name - The identifier name to quote
 * @returns The fully quoted identifier (e.g., "First Name" or "user""name")
 *
 * @example
 * ```ts
 * quoteIdentifier('user_name') // '"user_name"'
 * quoteIdentifier('user"name') // '"user""name"'
 * quoteIdentifier('First Name') // '"First Name"'
 * quoteIdentifier('select') // '"select"' (prevents keyword conflict)
 * ```
 */
export function quoteIdentifier(name: string): string {
  return `"${escapeDoubleQuotes(name)}"`;
}

// ============================================================================
// FILENAME SANITIZATION
// ============================================================================

/**
 * Sanitize filename for Content-Disposition headers
 *
 * Security considerations addressed:
 * 1. Removing newlines/carriage returns (HTTP header injection)
 * 2. Removing/escaping quotes (could break out of filename="...")
 * 3. Removing path traversal characters
 * 4. Limiting length to prevent buffer issues
 * 5. Replacing non-ASCII with safe alternatives
 *
 * @param filename - The raw filename to sanitize
 * @returns Safe filename for Content-Disposition header
 */
export function sanitizeFilename(filename: string): string {
  if (!filename || typeof filename !== 'string') {
    return 'download';
  }

  let sanitized = filename
    // Remove newlines and carriage returns (header injection)
    .replace(/[\r\n]/g, '')
    // Remove quotes (could break out of filename="...")
    .replace(/["']/g, '')
    // Remove path separators (path traversal)
    .replace(/[/\\]/g, '_')
    // Remove other potentially dangerous characters
    .replace(/[<>:"|?*]/g, '_')
    // Replace multiple underscores with single
    .replace(/_+/g, '_')
    // Trim underscores from start/end
    .replace(/^_+|_+$/g, '');

  // Ensure we have something left
  if (!sanitized) {
    return 'download';
  }

  // Limit length (leave room for extension)
  if (sanitized.length > 180) {
    sanitized = sanitized.substring(0, 180);
  }

  return sanitized;
}

/**
 * Sanitize a filename for use in S3/R2 object key paths.
 *
 * Builds on `sanitizeFilename` (which handles header injection, path traversal,
 * and dangerous characters) then additionally strips any character not in the
 * S3 path-safe allowlist: `[a-zA-Z0-9_.-]`.
 *
 * @param filename - The raw filename to sanitize
 * @returns Safe filename for use in S3/R2 paths
 */
export function sanitizePathFilename(filename: string): string {
  return sanitizeFilename(filename).replace(/[^a-zA-Z0-9_.-]/g, '_');
}
